import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SelectMe from 'react-select-me';

import { formatCurrency, formatDate, formatValue } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { BoitageAssignment, BoitageDocumentKind, BoitageWindow } from '../../../@types/Boitage';
import { BOITAGE_STATUS   } from '../../../@types/Boitage';
import type { Company, ToolsTimeline, ToolsTimelineYear } from '../../../@types/Company';
import { useDvfYears } from '../../../hooks/useDvfYears';
import type { RootState } from '../../../store/store';
import { deleteCompanyBoitageAssignmentsBulk, getCompanyBoitageAssignments, getCompanyBoitageWindowDocument, getCompanyBoitageWindows, patchCompanyBoitageWindow, patchCoproBoitageAllowed, postCompanyBoitageAssignmentsBulk } from '../../../utils/api';
import { JWTAuthorization, JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable/MUITable.module';
import { showToast } from '../../common/Notification';
import RedirectionCoproButtons from '../../common/RedirectionCoproButtons';
import BoitageDocumentsPanel from './components/BoitageDocumentsPanel.module';

type Filters = {
  ventesMin: number | null;
  ventesMax: number | null;
  caMin: number | null;
  caMax: number | null;
  commMin: number | null;
  commMax: number | null;
  avgSaleMin: number | null;
  scisMin: number | null;
  transactionnaire: string | null;
};

type CoproSalesRow = {
  id: number;
  immatriculation: string;
  name: string;
  address: string;
  cp: string;
  city: string;
  salesAmount: number;
  nbLots: number;
  nbSales: number;
  m2ForNbSales: number | null;
  salesAvgForM2: number | null;
  boitageAllowed: boolean;
};

const TRANSACTIONNAIRE_FONCTION_TYPE = 3; // see FonctionTypeUserEnum.TRANSACTIONNAIRE

const WindowDocumentDownloadButton: React.FC<{ window: BoitageWindow; kind: BoitageDocumentKind; label: string }> = ({ window, kind, label }) => {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);
  const enabled = Boolean(window.documents?.[kind]);

  const handleClick = async() => {
    if (!enabled || !company?.id || !jwt) { return; }
    try {
      const resp = await fetch(getCompanyBoitageWindowDocument(company.id, window.year, window.eventIndex, kind), {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${label.replaceAll(/\s+/g, '-')}-${window.year}-${window.eventIndex}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(`Error downloading boitage window document (${kind}, ${window.year}:${window.eventIndex}):`, e);
      showToast('error', 'Erreur lors du téléchargement du modèle.');
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={!enabled}
      className={`rounded px-3 py-1.5 text-xs font-medium transition-colors ${enabled ? 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50' : 'cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400'}`}
      title={enabled ? `Télécharger ${label} pour cette date` : `${label} non configuré pour cette date — déposez-le dans Paramétrage › Contenu`}
    >
      ⬇ {label}
    </button>
  );
};

function BoitageQualification() {
  const navigate = useNavigate();
  const jwt = useSelector((state: RootState) => state.auth.token);
  const company = useSelector((state: RootState) => state.auth.company);

  const { yearsOptions } = useDvfYears({ startYear: 2014 });

  const [rows, setRows] = useState<CoproSalesRow[]>([]);
  const [status, setStatus] = useState<AsyncStatus>('IDLE');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sciCountByCopro, setSciCountByCopro] = useState<Record<number, number>>({});
  const [transactionnaireByCopro, setTransactionnaireByCopro] = useState<Record<number, string[]>>({});
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Filters>({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
  const [commissionRate] = useState<number>(company?.commission ?? 5);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [assignmentsByCoproId, setAssignmentsByCoproId] = useState<Record<number, { 1?: { plannedLabel: string | null }, 2?: { plannedLabel: string | null } }>>({});
  const [globalStats, setGlobalStats] = useState<{ nbSales: number; salesAmount: number } | null>(null);
  const [boitageAllowedByCoproId, setBoitageAllowedByCoproId] = useState<Record<number, boolean>>({});

  const headers = [
    { title: 'Immatriculation' },
    { title: 'Nom' },
    { title: 'Adresse' },
    { title: 'Ville' },
    { title: 'Montant total des ventes' },
    { title: 'Commission' },
    { title: 'Nb lots hab / local pro' },
    { title: 'Nb ventes' },
    { title: 'm2 moyen / vente' },
    { title: 'Prix moyen / m2' },
    { title: 'Nb SCI' },
    { title: 'Transactionnaire' },
    { title: 'Boitage autorisé' },
  ];

  const fetchRows = async() => {
    if (!company?.id || !jwt || selectedYears.length === 0) {return;}
    setStatus('PENDING');
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      selectedYears.forEach((y) => params.append('years[]', String(y)));
      const resp = await fetch(`/api/companies/${company.id}/stats/copros/sales/years?${params.toString()}`, {
        headers: { Authorization: JWTAuthorization(jwt) },
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();

      type SalesApiRow = {
        id: number;
        immatriculation?: string;
        name?: string;
        address?: string;
        cp?: string;
        city?: string;
        salesAmount?: number;
        nbLots?: number;
        nbSales?: number;
        m2ForNbSales?: number | null;
        salesAvgForM2?: number | null;
        boitageAllowed?: boolean;
      };

      let results: SalesApiRow[] = [];
      if (Array.isArray(data)) {
        results = data;
      } else if (data.results) {
        results = data.results;
        setGlobalStats(data.global);
      }

      const mapped: CoproSalesRow[] = (results || []).map((r: SalesApiRow) => ({
        id: r.id,
        immatriculation: r.immatriculation,
        name: r.name,
        address: r.address,
        cp: r.cp,
        city: r.city,
        salesAmount: Number(r.salesAmount || 0),
        nbLots: Number(r.nbLots || 0),
        nbSales: Number(r.nbSales || 0),
        m2ForNbSales: r.m2ForNbSales != null ? Number(r.m2ForNbSales) : null,
        salesAvgForM2: r.salesAvgForM2 != null ? Number(r.salesAvgForM2) : null,
        boitageAllowed: r.boitageAllowed ?? true,
      }));
      setRows(mapped);
      // Update boitageAllowed map
      const allowedMap: Record<number, boolean> = {};
      mapped.forEach((r) => { allowedMap[r.id] = r.boitageAllowed; });
      setBoitageAllowedByCoproId(allowedMap);
      setStatus('SUCCESS');
    } catch (e) {
      console.error('Error fetching sales rows :', e);
      setStatus('ERROR');
      setErrorMessage('Une erreur est survenue lors du chargement des données.');
      showToast('error', 'Une erreur est survenue lors du chargement des données.');
    }
  };

  const [windows, setWindows] = useState<BoitageWindow[] | null>(null);
  const fetchWindows = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(getCompanyBoitageWindows(company.id), JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: BoitageWindow[] = await resp.json();


      setWindows(data);
    } catch (e) {
      console.error('Error fetching windows :', e);
    }
  };

  useEffect(() => { fetchWindows(); }, [company?.id, jwt]);

  // Activation flags from planning (current year with fallback)
  const currentYear = useMemo(() => new Date().getFullYear(), []);
  type BoitageActivation = { 'Boîtage 1': boolean; 'Boîtage 2': boolean } | null;

  const activation = useMemo((): BoitageActivation => {
    const timeline = (company as Company | null)?.toolsTimeline ?? null;
    if (!timeline) { return null; }
    const years = Object.keys(timeline)
      .map((k) => Number(k))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
    const candidates: number[] = [];
    if (years.includes(currentYear)) { candidates.push(currentYear); }
    const past = years.filter((y) => y < currentYear).sort((a, b) => b - a);
    const future = years.filter((y) => y > currentYear).sort((a, b) => a - b);
    candidates.push(...past, ...future);
    for (const y of candidates) {
      const yd: ToolsTimelineYear | undefined = (timeline as ToolsTimeline)[String(y)];
      if (!yd || typeof yd !== 'object') { continue; }
      const eventActivation = yd.eventActivation;
      const boitage = eventActivation?.boitage;
      if (boitage && Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 1') && Object.prototype.hasOwnProperty.call(boitage, 'Boîtage 2')) {
        return {
          'Boîtage 1': Boolean(boitage['Boîtage 1']),
          'Boîtage 2': Boolean(boitage['Boîtage 2']),
        };
      }
    }
    return null;
  }, [company, currentYear]);
  const isBoitage1Active = activation ? Boolean(activation['Boîtage 1']) : true;
  const isBoitage2Active = activation ? Boolean(activation['Boîtage 2']) : true;

  useEffect(() => {
    if (yearsOptions.length > 0 && selectedYears.length === 0) {
      const firstTwo = yearsOptions.slice(0, 2).map((o) => Number(o.value));
      setSelectedYears(firstTwo);
    }
  }, [yearsOptions, selectedYears.length]);

  // Fetch assignments for visible windows and create a mapping coproId -> { 1?: {plannedLabel}, 2?: {plannedLabel} }
  useEffect(() => {
    const run = async() => {
      if (!company?.id || !jwt || !windows) { return; }
      try {
        const results = await Promise.all(windows.map(async(w) => {
          const r = await fetch(getCompanyBoitageAssignments(company.id, Number(w.year), w.eventIndex), JWTHeader(jwt));
          if (!r.ok) { throw new Error(`${r.status}`); }
          const json: { results: BoitageAssignment[] } = await r.json();
          return { w, rows: json.results.map(a => ({ coproId: a.coproId, eventIndex: a.eventIndex as 1 | 2, plannedLabel: a.plannedLabel ?? null })) };
        }));
        const map: Record<number, { 1?: { plannedLabel: string | null }, 2?: { plannedLabel: string | null } }> = {};
        results.forEach((bucket) => {
          bucket.rows.forEach((a) => {
            if (!map[a.coproId]) { map[a.coproId] = {}; }
            if (a.eventIndex === 1) { map[a.coproId][1] = { plannedLabel: a.plannedLabel }; }
            if (a.eventIndex === 2) { map[a.coproId][2] = { plannedLabel: a.plannedLabel }; }
          });
        });
        setAssignmentsByCoproId(map);
      } catch (e) {
        console.error('Error fetching assignments :', e);
      }
    };
    run();
  }, [windows, company?.id, jwt]);

  // Additional maps for filters parity with detailed list
  const fetchSciCounts = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/copros/scis/stats`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data: Array<{ id: number; scis: { total: number } }> = await resp.json();
      const map: Record<number, number> = {};
      data.forEach((c) => { map[c.id] = c.scis?.total ?? 0; });
      setSciCountByCopro(map);
    } catch (e) {
      console.error('Error fetching SCI counts :', e);
    }
  };

  const fetchTransactionnaires = async() => {
    if (!company?.id || !jwt) {return;}
    try {
      const resp = await fetch(`/api/companies/${company.id}/validated-copros/attributions`, JWTHeader(jwt));
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      const data = await resp.json();
      const map: Record<number, string[]> = {};
      type AttributionItem = { fonctionType?: number; copro?: { id: number }; user?: { firstname?: string; lastname?: string } };
      (data?.results || []).forEach((a: AttributionItem) => {
        if (a?.fonctionType === TRANSACTIONNAIRE_FONCTION_TYPE && a?.copro?.id && a?.user) {
          const name = `${a.user.firstname} ${a.user.lastname ? a.user.lastname.toUpperCase() : ''}`.trim();
          if (!map[a.copro.id]) { map[a.copro.id] = []; }
          map[a.copro.id].push(name);
        }
      });
      setTransactionnaireByCopro(map);
    } catch (e) {
      console.error('Error fetching attributions :', e);
    }
  };

  const handleGoBack = () => {
    try { navigate(-1); } catch { navigate(ROUTES.HOME); }
  };

  const handleToggleBoitageAllowed = async(coproId: number) => {
    if (!jwt || !company?.id) { return; }
    const currentValue = boitageAllowedByCoproId[coproId] ?? true;
    const newValue = !currentValue;
    try {
      const resp = await fetch(patchCoproBoitageAllowed(coproId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
        body: JSON.stringify({ boitageAllowed: newValue }),
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      
      // If toggling to false (interdit), also unassign from both boitages
      if (!newValue && windows && windows.length > 0) {
        const currentAssignment = assignmentsByCoproId[coproId];
        const unassignPromises: Promise<Response>[] = [];
        
        // Unassign from Boitage 1 if assigned
        const window1 = windows.find(w => w.eventIndex === 1);
        if (window1 && currentAssignment && currentAssignment[1] && !window1.executedAt) {
          unassignPromises.push(
            fetch(deleteCompanyBoitageAssignmentsBulk(company.id), {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
              body: JSON.stringify({ year: window1.year, eventIndex: 1, coproIds: [coproId] }),
            }),
          );
        }
        
        // Unassign from Boitage 2 if assigned
        const window2 = windows.find(w => w.eventIndex === 2);
        if (window2 && currentAssignment && currentAssignment[2] && !window2.executedAt) {
          unassignPromises.push(
            fetch(deleteCompanyBoitageAssignmentsBulk(company.id), {
              method: 'DELETE',
              headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
              body: JSON.stringify({ year: window2.year, eventIndex: 2, coproIds: [coproId] }),
            }),
          );
        }
        
        // Execute all unassign requests
        if (unassignPromises.length > 0) {
          await Promise.all(unassignPromises);
          // Update local assignments state
          setAssignmentsByCoproId((prev) => {
            const next = { ...prev };
            delete next[coproId];
            return next;
          });
        }
      }
      
      // Update local state
      setBoitageAllowedByCoproId((prev) => ({ ...prev, [coproId]: newValue }));
      // Also update rows state to keep in sync
      setRows((prev) => prev.map((r) => r.id === coproId ? { ...r, boitageAllowed: newValue } : r));
      // If toggling to false, deselect this row if selected
      if (!newValue) {
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(coproId);
          return next;
        });
      }
      showToast('success', newValue ? 'Boitage autorisé pour cette copropriété.' : 'Boitage interdit pour cette copropriété.');
    } catch (e) {
      console.error('Error toggling boitage allowed :', e);
      showToast('error', 'Erreur lors de la mise à jour.');
    }
  };

  const filteredRows = useMemo(() => {
    const q = search.toLowerCase();
    const rate = commissionRate / 100;
    return rows.filter((r) => {
      const commission = r.salesAmount * rate;
      const txNames = transactionnaireByCopro[r.id] || [];
      const sciCount = sciCountByCopro[r.id] ?? 0;
      const avgSale = r.nbSales > 0 ? (r.salesAmount / r.nbSales) : 0;
      const basic = `${r.immatriculation} ${r.name} ${r.address} ${r.city} ${r.cp}`.toLowerCase();
      if (q && !basic.includes(q)) { return false; }
      if (filters.ventesMin != null && r.nbSales < filters.ventesMin) { return false; }
      if (filters.ventesMax != null && r.nbSales > filters.ventesMax) { return false; }
      if (filters.caMin != null && r.salesAmount < filters.caMin) { return false; }
      if (filters.caMax != null && r.salesAmount > filters.caMax) { return false; }
      if (filters.commMin != null && commission < filters.commMin) { return false; }
      if (filters.commMax != null && commission > filters.commMax) { return false; }
      if (filters.avgSaleMin != null && avgSale < filters.avgSaleMin) { return false; }
      if (filters.scisMin != null && sciCount < filters.scisMin) { return false; }
      if (filters.transactionnaire && !txNames.includes(filters.transactionnaire)) { return false; }
      return true;
    });
  }, [rows, search, filters, commissionRate, transactionnaireByCopro, sciCountByCopro]);

  const kpis = useMemo(() => {
    const filtersActive = Boolean(
      search ||
      filters.ventesMin != null ||
      filters.ventesMax != null ||
      filters.caMin != null ||
      filters.caMax != null ||
      filters.commMin != null ||
      filters.commMax != null ||
      filters.avgSaleMin != null ||
      filters.scisMin != null ||
      filters.transactionnaire,
    );

    const totalCopros = filteredRows.length;
    const totalLotsEnGestion = filteredRows.reduce((s, r) => s + (r.nbLots || 0), 0);
    
    let totalSalesAmount = 0;
    let totalLotsVendus = 0;

    if (!filtersActive && globalStats) {
      totalSalesAmount = globalStats.salesAmount;
      totalLotsVendus = globalStats.nbSales;
    } else {
      totalSalesAmount = filteredRows.reduce((s, r) => s + (r.salesAmount || 0), 0);
      totalLotsVendus = filteredRows.reduce((s, r) => s + (r.nbSales || 0), 0);
    }

    const avgSalePrice = totalLotsVendus > 0 ? Math.round(totalSalesAmount / totalLotsVendus) : 0;
    return { totalCopros, totalLotsEnGestion, totalLotsVendus, avgSalePrice, totalSalesAmount };
  }, [filteredRows, search, filters, globalStats]);

  const handleIndividualAssign = async(coproId: number, eventIndex: 1 | 2) => {
    if (!company?.id || !jwt || !windows || windows.length < 1) { return; }
    const target = windows.find(w => w.eventIndex === eventIndex);
    if (!target) { return; }
    if (target.executedAt) {
      showToast('warning', 'Ce boîtage est déjà marqué comme effectué. Les affectations ne sont plus modifiables.');
      return;
    }

    const currentAssignment = assignmentsByCoproId[coproId];
    const isCurrentlyAssigned = Boolean(currentAssignment && currentAssignment[eventIndex]);

    try {
      if (isCurrentlyAssigned) {
        // Unassign: call DELETE bulk endpoint
        const resp = await fetch(deleteCompanyBoitageAssignmentsBulk(company.id), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
          body: JSON.stringify({ year: target.year, eventIndex: target.eventIndex, coproIds: [coproId] }),
        });
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        showToast('success', 'Copropriété retirée du boîtage.');
      } else {
        // Assign: call POST bulk endpoint
        const resp = await fetch(postCompanyBoitageAssignmentsBulk(company.id), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
          body: JSON.stringify({ year: target.year, eventIndex: target.eventIndex, coproIds: [coproId] }),
        });
        if (!resp.ok) { throw new Error(`${resp.status}`); }
        showToast('success', 'Copropriété affectée au boîtage.');
      }
      fetchWindows();
    } catch (e) {
      console.error('Error individual assign :', e);
      showToast('error', 'Erreur lors de l\'affectation.');
    }
  };

  const dataForTable = useMemo(() => {
    const rate = commissionRate / 100;
    return filteredRows.map((r) => {
      const isBoitageAllowed = boitageAllowedByCoproId[r.id] ?? true;
      return {
        'VALIDE': isBoitageAllowed ? selectedIds.has(r.id) : false,
        '_checkboxDisabled': !isBoitageAllowed,
        '_coproId': r.id,
        'Immatriculation': r.immatriculation,
        'Nom': (
          <div className="flex items-center gap-2">
            <RedirectionCoproButtons coproId={r.id} />
            <span className="font-semibold">{r.name}</span>
          </div>
        ),
        'Adresse': r.address,
        'Ville': `${r.city} - ${r.cp}`,
        'Montant total des ventes': formatCurrency(r.salesAmount),
        'Commission': formatCurrency(r.salesAmount * rate),
        'Nb lots hab / local pro': r.nbLots,
        'Nb ventes': r.nbSales,
        'm2 moyen / vente': r.m2ForNbSales != null ? formatValue(r.m2ForNbSales) : '-',
        'Prix moyen / m2': r.salesAvgForM2 != null ? formatCurrency(r.salesAvgForM2) : '-',
        'Nb SCI': sciCountByCopro[r.id] ?? '-',
        'Transactionnaire': (
          <span className="text-xs font-medium text-purple-600">{(transactionnaireByCopro[r.id] || []).join(', ')}</span>
        ),
        'Boitage autorisé': (() => {
          return (
            <button
              onClick={() => handleToggleBoitageAllowed(r.id)}
              className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
                isBoitageAllowed
                  ? 'border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                  : 'border-orange-300 bg-orange-100 text-orange-700 hover:bg-orange-200'
              }`}
            >
              {isBoitageAllowed ? 'Règle copro boitage autorisé' : 'Règle copro boitage interdit'}
            </button>
          );
        })(),
        'Boîtage 1': (() => {
          const a = assignmentsByCoproId[r.id];
          const isAssigned = Boolean(a && a[1]);
          const targetWindow = windows?.find(w => w.eventIndex === 1);
          const label = (a && a[1]?.plannedLabel) || (targetWindow?.plannedLabel) || '';
          const year = targetWindow?.plannedDate ? new Date(`${targetWindow.plannedDate}T00:00:00`).getFullYear() : (targetWindow?.year || '');
          const suffix = label ? `${label}${year ? ` ${year}` : ''}` : '';
          const text = suffix ? (isAssigned ? `Programmé pour ${suffix}` : `Non inclu pour ${suffix}`) : (isAssigned ? 'Programmé' : 'Non inclu');
          const isDisabled = !isBoitage1Active || !isBoitageAllowed;
          const disabledReason = !isBoitageAllowed ? 'Boitage interdit pour cette copropriété' : (!isBoitage1Active ? 'Boîtage 1 désactivé dans le planning' : '');
          return (
            <button
              onClick={() => handleIndividualAssign(r.id, 1)}
              disabled={isDisabled}
              title={disabledReason}
              className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
                isAssigned
                  ? 'border-green-300 bg-green-100 text-green-700'
                  : 'border-red-300 bg-red-100 text-red-700'
              } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-green-200'}`}
            >
              {text}
            </button>
          );
        })(),
        'Boîtage 2': (() => {
          const a = assignmentsByCoproId[r.id];
          const isAssigned = Boolean(a && a[2]);
          const targetWindow = windows?.find(w => w.eventIndex === 2);
          const label = (a && a[2]?.plannedLabel) || (targetWindow?.plannedLabel) || '';
          const year = targetWindow?.plannedDate ? new Date(`${targetWindow.plannedDate}T00:00:00`).getFullYear() : (targetWindow?.year || '');
          const suffix = label ? `${label}${year ? ` ${year}` : ''}` : '';
          const text = suffix ? (isAssigned ? `Programmé pour ${suffix}` : `Non inclu pour ${suffix}`) : (isAssigned ? 'Programmé' : 'Non inclu');
          const isDisabled = !isBoitage2Active || !isBoitageAllowed;
          const disabledReason = !isBoitageAllowed ? 'Boitage interdit pour cette copropriété' : (!isBoitage2Active ? 'Boîtage 2 désactivé dans le planning' : '');
          return (
            <button
              onClick={() => handleIndividualAssign(r.id, 2)}
              disabled={isDisabled}
              title={disabledReason}
              className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
                isAssigned
                  ? 'border-green-300 bg-green-100 text-green-700'
                  : 'border-red-300 bg-red-100 text-red-700'
              } ${isDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-green-200'}`}
            >
              {text}
            </button>
          );
        })(),
      };
    });
  }, [filteredRows, commissionRate, sciCountByCopro, transactionnaireByCopro, selectedIds, assignmentsByCoproId, windows, boitageAllowedByCoproId]);

  const handleBulkAssign = async(eventIndex: 1 | 2) => {
    if (!company?.id || !jwt || !windows || windows.length < 1) { return; }
    const target = windows.find(w => w.eventIndex === eventIndex);
    if (!target) { return; }
    if (target.executedAt) {
      showToast('warning', 'Ce boîtage est déjà marqué comme effectué. Les affectations ne sont plus modifiables.');
      return;
    }
    const ids = [...selectedIds];
    if (ids.length === 0) { showToast('warning', 'Sélectionnez au moins une copropriété.'); return; }
    try {
      const resp = await fetch(postCompanyBoitageAssignmentsBulk(company.id), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
        body: JSON.stringify({ year: target.year, eventIndex: target.eventIndex, coproIds: ids }),
      });
      if (!resp.ok) { throw new Error(`${resp.status}`); }
      showToast('success', 'Affectation enregistrée.');
      setSelectedIds(new Set());
      fetchWindows();
    } catch (e) {
      console.error('Error bulk assign :', e);
      showToast('error', 'Erreur lors de l\'affectation.');
    }
  };

  // Local button with follow-mouse tooltip when disabled
  const MarkDoneButton: React.FC<{ plannedDate: string; executedAt?: string | null; onSubmit: (executedIso: string) => Promise<void> | void }> = ({ plannedDate, executedAt, onSubmit }) => {
    const [tooltip, setTooltip] = useState<{ visible: boolean; x: number; y: number }>({ visible: false, x: 0, y: 0 });
    const planned = useMemo(() => new Date(`${plannedDate}T00:00:00`), [plannedDate]);
    const start = useMemo(() => { const d = new Date(planned); d.setDate(d.getDate() - 15); return d; }, [planned]);
    const end = useMemo(() => { const d = new Date(planned); d.setDate(d.getDate() + 30); return d; }, [planned]);
    const inWindow = useMemo(() => { const now = new Date(); return now >= start && now <= end; }, [start, end]);
    const [selectedDate, setSelectedDate] = useState<string>(() => executedAt ? executedAt.slice(0, 10) : plannedDate);

    useEffect(() => {
      setSelectedDate(executedAt ? executedAt.slice(0, 10) : plannedDate);
    }, [executedAt, plannedDate]);

    const handleMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
      if (inWindow) { return; }
      const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
      setTooltip({ visible: true, x: e.clientX - rect.left + 12, y: e.clientY - rect.top + 12 });
    };

    const handleMouseLeave = () => setTooltip((t) => ({ ...t, visible: false }));

    if (executedAt) {
      const formatted = formatDate(executedAt);
      return (
        <span className="text-xs font-semibold text-green-700 uppercase">EFFECTUÉ LE {formatted}</span>
      );
    }

    const handleSubmit = () => {
      if (!inWindow) { return; }
      if (!selectedDate) {
        showToast('warning', 'Sélectionnez une date.');
        return;
      }
      const iso = new Date(`${selectedDate}T00:00:00`).toISOString();
      onSubmit(iso);
    };

    return (
      <div className="relative inline-flex items-center gap-2" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseMove}>
        <input
          type="date"
          className={`rounded border px-2 py-1 text-xs transition-colors ${inWindow ? 'bg-white text-slate-700' : 'cursor-not-allowed bg-gray-100 text-gray-400'}`}
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          disabled={!inWindow}
        />
        <button
          className={`rounded px-3 py-1.5 text-xs text-white transition-colors ${inWindow ? 'bg-green-600 hover:bg-green-700' : 'cursor-not-allowed bg-gray-300'}`}
          disabled={!inWindow}
          onClick={handleSubmit}
        >
          Marquer comme effectué
        </button>
        {!inWindow && tooltip.visible && (
          <div
            className="pointer-events-none absolute z-10 rounded bg-gray-800 px-2 py-1 text-[11px] whitespace-nowrap text-white shadow-md"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            Vous n'etes pas encore dans cette fenetre de distribution
          </div>
        )}
      </div>
    );
  };

  const allVisibleSelected = useMemo(() => {
    const selectableRows = filteredRows.filter((r) => boitageAllowedByCoproId[r.id] !== false);
    if (selectableRows.length === 0) { return false; }
    for (const r of selectableRows) {
      if (!selectedIds.has(r.id)) { return false; }
    }
    return true;
  }, [filteredRows, selectedIds, boitageAllowedByCoproId]);

  const handleToggleSelectVisible = () => {
    const selectableRows = filteredRows.filter((r) => boitageAllowedByCoproId[r.id] !== false);
    if (selectableRows.length === 0) { setSelectedIds(new Set()); return; }
    if (allVisibleSelected) {
      // Deselect only visible selectable rows
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((r) => next.delete(r.id));
        return next;
      });
    } else {
      // Select all visible selectable rows (skip boitage interdit)
      setSelectedIds((prev) => {
        const next = new Set(prev);
        selectableRows.forEach((r) => next.add(r.id));
        return next;
      });
    }
  };

  useEffect(() => { fetchRows(); fetchSciCounts(); fetchTransactionnaires(); }, [company?.id, jwt, selectedYears.join(',')]);

  const resetFilters = () => {
    setFilters({ ventesMin: null, ventesMax: null, caMin: null, caMax: null, commMin: null, commMax: null, avgSaleMin: null, scisMin: null, transactionnaire: null });
  };

  return (
    <div className="rounded-lg bg-white p-4 shadow-md md:p-8">
      <div className="mb-4 flex items-center justify-between">
        <button onClick={() => handleGoBack()} className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark">
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
      </div>
      <BoitageDocumentsPanel />

      <div className="mb-6 rounded-2xl p-6 text-white" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #475569 100%)' }}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold md:text-3xl">QUALIFICATION BOÎTAGE</h1>
            <div className="opacity-90">Planifiez vos boîtages avec les fenêtres issues du planning</div>
          </div>
        </div>
      </div>

      {/* Windows summary (moved up) */}
      <div className="mb-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
        {!windows && (<Badge mode="warning" text="Chargement du planning..." />)}
        {windows && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {(!isBoitage1Active || !isBoitage2Active) && (
              <div className="md:col-span-2">
                {!isBoitage1Active && (
                  <div className="mb-2"><Badge mode="warning" text="Boîtage 1 désactivé dans le planning." /></div>
                )}
                {!isBoitage2Active && (
                  <div className="mb-2"><Badge mode="warning" text="Boîtage 2 désactivé dans le planning." /></div>
                )}
              </div>
            )}
            {windows.map(w => {
              const planned = new Date(`${w.plannedDate}T00:00:00`);
              const start = new Date(planned);
              start.setDate(start.getDate() - 15);
              const end = new Date(planned);
              end.setDate(end.getDate() + 30);
              const now = new Date();
              const inWindow = now >= start && now <= end;
              const headerText = `Date boitage ${w.eventIndex} ${inWindow ? 'en cours' : 'prévue'}`;
              return (
                <div key={`${w.year}-${w.eventIndex}`} className="rounded-lg border bg-white p-4">
                  <div className="text-xs font-semibold text-slate-500 uppercase">{headerText}</div>
                  <div className="text-xl font-bold text-slate-800">{`${w.plannedLabel.toUpperCase()} ${planned.getFullYear()}`}</div>
                  <div className="mt-1 text-sm text-slate-700">Documents à prévoir: <span className="font-semibold">{w.docsPlanned.toLocaleString('fr-FR')}</span></div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <WindowDocumentDownloadButton window={w} kind="stop-pub" label="Modèle stop pub" />
                    <WindowDocumentDownloadButton window={w} kind="sans-stop-pub" label="Modèle sans stop pub" />
                  </div>
                  <div className="mt-3 flex gap-2">
                    <MarkDoneButton
                      plannedDate={w.plannedDate}
                      executedAt={w.executedAt}
                      onSubmit={async(executedIso) => {
                        if (!company?.id || !jwt) { return; }
                        try {
                          const resp = await fetch(patchCompanyBoitageWindow(company.id), {
                            method: 'PATCH',
                            headers: { 'Content-Type': 'application/json', Authorization: JWTAuthorization(jwt) },
                            body: JSON.stringify({ year: Number(w.year), eventIndex: w.eventIndex, status: BOITAGE_STATUS.DONE, executedAt: executedIso }),
                          });
                          if (!resp.ok) { throw new Error(`${resp.status}`); }
                          showToast('success', 'Boîtage marqué comme effectué.');
                          fetchWindows();
                        } catch (e) {
                          console.error('Error bulk window mark done :', e);
                          showToast('error', "Erreur lors de la mise à jour 'effectué'.");
                        }
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:!grid-cols-5">
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalCopros}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Nombre de Copropriétés</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalLotsEnGestion}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Total Lots en Gestion</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : kpis.totalLotsVendus}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Lots Vendus {selectedYears.length > 0 ? (selectedYears.length === 1 ? `${selectedYears[0]}` : `${Math.max(...selectedYears)}-${Math.min(...selectedYears)}`) : ''}</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : formatCurrency(kpis.avgSalePrice)}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Prix Moyen des Ventes</div>
        </div>
        <div className="rounded-xl border bg-white p-4">
          <div className="text-3xl font-bold text-slate-800">{status === 'PENDING' ? '-' : formatCurrency(kpis.totalSalesAmount)}</div>
          <div className="text-xs font-semibold text-slate-500 uppercase">Volume Transactions {selectedYears.length > 0 ? (selectedYears.length === 1 ? `${selectedYears[0]}` : `${Math.max(...selectedYears)}-${Math.min(...selectedYears)}`) : ''}</div>
        </div>
      </div>

      {/* Inline filters (parity with detailed list) */}
      <div className="mb-3 rounded-lg border bg-white shadow-sm">
        <div className="p-5">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:gap-6">
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="magnifying glass">🔍</span> Rechercher :</label>
              <input className="w-64 rounded border px-3 py-2 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nom, ville, immatriculation..." />
            </div>
            <div className="flex items-start gap-2">
              <label className="mt-2 text-sm font-medium text-slate-600"><span role="img" aria-label="calendar">📅</span> Années :</label>
              <div className="min-w-[220px]">
                <SelectMe
                  id="years-select"
                  name="years"
                  value={selectedYears.map((y) => ({ value: String(y), label: String(y) }))}
                  onChange={(selectedOptions) => setSelectedYears(selectedOptions.map((o) => Number(o.value)))}
                  options={yearsOptions}
                  multiple
                  closeOnSelect={false}
                  listPosition="bottom"
                  placeholder="Sélectionnez une ou plusieurs années"
                />
              </div>
            </div>
          </div>

          <div className="mt-4 rounded border p-4" style={{ background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', borderColor: '#3b82f6' }}>
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="chart">📊</span> Nb ventes:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
                <span>à</span>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.ventesMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, ventesMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">💰</span> CA:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.caMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, caMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="target">🎯</span> Commission:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
                <span>à</span>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.commMax ?? ''} onChange={(e) => setFilters((f) => ({ ...f, commMax: e.target.value ? Number(e.target.value) : null }))} placeholder="Max €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="person">👤</span> Transactionnaire:</label>
                <select className="w-40 rounded border px-2 py-1 text-sm" value={filters.transactionnaire ?? ''} onChange={(e) => setFilters((f) => ({ ...f, transactionnaire: e.target.value || null }))}>
                  <option value="">Tous</option>
                  {[...new Set(Object.values(transactionnaireByCopro).flat().filter(Boolean))].map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="emoji">🏷️</span> Prix moyen/vente:</label>
                <input type="number" className="w-24 rounded border px-2 py-1 text-sm" value={filters.avgSaleMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, avgSaleMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min €" />
              </div>
              <div className="flex items-center gap-2 text-sm">
                <label className="font-medium text-slate-700"><span role="img" aria-label="factory">🏢</span> Nb SCI min:</label>
                <input type="number" className="w-20 rounded border px-2 py-1 text-sm" value={filters.scisMin ?? ''} onChange={(e) => setFilters((f) => ({ ...f, scisMin: e.target.value ? Number(e.target.value) : null }))} placeholder="Min" />
              </div>
              <div className="ml-auto flex items-center gap-2">
                <button className="rounded bg-slate-500 px-3 py-1.5 text-sm text-white hover:bg-slate-600" onClick={resetFilters}><span role="img" aria-label="refresh">🔄</span> Réinitialiser</button>
              </div>
            </div>

            {/** removed predefined qualification quick filters **/}
          </div>
        </div>
      </div>
      

      {/* Table */}
      {status === 'ERROR' && (
        <div className="p-6"><Badge mode="error" text={errorMessage || 'Erreur'} /></div>
      )}
      {status === 'PENDING' && (
        <div className="p-6"><Badge mode="warning" text="Chargement..." /></div>
      )}
      {status === 'SUCCESS' && (
        <div className="p-4">
          {/* Selection and bulk assignment controls */}
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <button className="rounded bg-slate-200 px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-300" onClick={handleToggleSelectVisible}>
              {allVisibleSelected ? 'Tout désélectionner (liste visible)' : 'Tout sélectionner (liste visible)'}
            </button>
            <span className="ml-1 text-xs text-slate-600">Sélection: {selectedIds.size} élément(s)</span>
            <div className="ml-auto flex items-center gap-2">
              <button
                className={`rounded px-3 py-1.5 text-sm text-white ${isBoitage1Active ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-gray-300'}`}
                onClick={() => handleBulkAssign(1)}
                disabled={!isBoitage1Active}
                title={isBoitage1Active ? '' : 'Boîtage 1 désactivé dans le planning'}
              >
                Affecter au boîtage 1
              </button>
              <button
                className={`rounded px-3 py-1.5 text-sm text-white ${isBoitage2Active ? 'bg-orange-600 hover:bg-orange-700' : 'cursor-not-allowed bg-gray-300'}`}
                onClick={() => handleBulkAssign(2)}
                disabled={!isBoitage2Active}
                title={isBoitage2Active ? '' : 'Boîtage 2 désactivé dans le planning'}
              >
                Affecter au boîtage 2
              </button>
            </div>
          </div>
          <MUITable
            data={dataForTable}
            headers={[...headers, { title: 'Boîtage 1' }, { title: 'Boîtage 2' }]}
            tableClass="min-w-full bg-white border text-xs"
            dataChangeCallback={(checked, _index, item) => {
              const coproId = item._coproId as number | undefined;
              if (coproId == null) { return; }
              if (boitageAllowedByCoproId[coproId] === false) { return; }
              setSelectedIds((prev) => {
                const next = new Set(prev);
                if (checked) { next.add(coproId); } else { next.delete(coproId); }
                return next;
              });
            }}
            sortableColumns={['Nb ventes', 'Montant total des ventes', 'Commission', 'Nb lots hab / local pro']}
            defaultSortKey="Nb ventes"
            defaultSortDirection="desc"
          />
        </div>
      )}
    </div>
  );
}

export default BoitageQualification;


